/* AddReportForm.jsx */
import React, { useEffect, useState, useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../../AbstractElements';
import { useLoader } from '../../../_helper/LoaderContext';
import { getUnitList, getUnitMasterList } from '../../UnitManagment/service/unit-service';
import { addGlobalParameter, addParameter, globalParameter } from './service/parameter-service';
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import { Button, Input, Label } from 'reactstrap';
import Select, { components } from 'react-select';

// Custom Checkbox Option
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center py-1">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};
// Select All Option
const allOption = { label: "Select All", value: "*" };

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '44px',
        height: '44px',
        overflow: 'hidden',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '44px',
        padding: '0 8px',
        overflowX: 'auto',        // This enables horizontal scroll
        overflowY: 'hidden',
        flexWrap: 'nowrap',       // Important: prevents wrapping to new line
        whiteSpace: 'nowrap',
        scrollbarWidth: 'thin',   // Firefox
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#1b255b',
        margin: '2px',
        flexShrink: 0,  // Prevents shrinking
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#fff',
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#d32f2f',
        ':hover': {
            backgroundColor: '#ffcdd2',
            color: 'white',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }),
};

const AddReportForm = () => {
    const [formData, setFormData] = useState({
        parameterName: '',
        parameterCode: '',
        measurementType: 'quantitative',
        unit: '',
        ranges: [
            {
                gender: '',
                minAgeGroup: '',
                referenceText: '',
                // maxAgeGroup: 'Adult',
                minValue: '',
                maxValue: '',
                note: '',
            },
        ],
    });

    const [errors, setErrors] = useState({});
    const showMinMax = formData.measurementType === 'quantitative';
    const { setLoading } = useLoader()
    const [unit, setUnit] = useState([])
    const navigate = useNavigate();
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [imported, setImported] = useState("add")


    // Global units from master list
    const [unitMaster, setUnitMaster] = useState([]);

    // Selected global units (for filtering or submission)
    const [selectedGlobalUnits, setSelectedGlobalUnits] = useState([]);

    // Transform unitMaster into react-select format
    const unitOptions = useMemo(() => {
        const options = unitMaster.map(u => ({
            label: u.name,
            value: u._id,
            description: u.description || ""
        }));
        return unitMaster.length > 0 ? [allOption, ...options] : [];
    }, [unitMaster]);

    useEffect(() => {
        getGlobalParameter()
    }, [])

    // Fetch global units
    const getGlobalParameter = async () => {
        setLoading(true);
        try {
            const res = await globalParameter();
            if (res.data.success) {
                setUnitMaster(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching global units:", error);
        } finally {
            setLoading(false);
        }
    };

    // Submit selected global units
    const handleSubmitSelected = async () => {
        const selectedIds = selectedGlobalUnits
            .filter(item => item.value !== "*")
            .map(item => item.value);

        if (selectedIds.length === 0) {
            toast.info("No units selected");
            return;
        }
        const payload = {
            masterParameterIds: selectedIds
        }
        setLoading(true)
        try {
            const res = await addGlobalParameter(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                // getList();
                navigate(`${process.env.PUBLIC_URL}/parameter-list`);
                setSelectedGlobalUnits([]);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        getUintMaster()
    }, [])

    const getUintMaster = async () => {
        setLoading(true)
        try {
            const res = await getUnitMasterList()
            if (res.data.success == true) {
                setUnit(res.data.data)
            }
        }
        catch (error) { console.log(error) }
        finally {
            setLoading(false)
        }
    }

    /* ---------- Change Handlers ---------- */
    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleRangeChange = (idx, field, value) => {
        const newRanges = [...formData.ranges];
        newRanges[idx] = { ...newRanges[idx], [field]: value };
        setFormData(prev => ({ ...prev, ranges: newRanges }));
        // Clear related errors
        setErrors(prev => {
            const copy = { ...prev };
            delete copy[`range_${idx}_${field}`];
            return copy;
        });
    };

    /* ---------- Add / Remove Range ---------- */
    const addRange = () => {
        setFormData(prev => ({
            ...prev,
            ranges: [
                ...prev.ranges,
                {
                    gender: '',
                    minAgeGroup: '',
                    // maxAgeGroup: 'Adult',
                    referenceText: '',
                    minValue: '',
                    maxValue: '',
                    note: '',
                },
            ],
        }));
    };

    const removeRange = idx => {
        setFormData(prev => ({
            ...prev,
            ranges: prev.ranges.filter((_, i) => i !== idx),
        }));
    };

    /* ---------- Validation ---------- */
    const validate = () => {
        const newErr = {};

        // Top-level required fields
        if (!formData.parameterName.trim())
            newErr.parameterName = 'Parameter name is required';
        if (!formData.parameterCode.trim())
            newErr.parameterCode = 'Parameter code is required';
        // if (!formData.unit) newErr.unit = 'Please select a unit';

        // Validate each range
        formData.ranges.forEach((r, i) => {
            // Gender
            if (!r.gender) newErr[`range_${i}_gender`] = 'Gender is required';

            // Age Group
            if (!r.minAgeGroup) newErr[`range_${i}_minAgeGroup`] = 'age group is required';

            // Min / Max Value (only for quantitative)
            if (showMinMax) {
                if (!r.minValue.trim())
                    newErr[`range_${i}_minValue`] = 'Min value is required';
                if (!r.maxValue.trim())
                    newErr[`range_${i}_maxValue`] = 'Max value is required';

                // Min <= Max numeric check
                const min = parseFloat(r.minValue);
                const max = parseFloat(r.maxValue);
                if (!isNaN(min) && !isNaN(max) && min > max) {
                    newErr[`range_${i}_minValue`] = 'Min value must be ≤ Max value';
                }
            }

            // Cross-group validation: Child cannot be after Adult
            const groupOrder = { Child: 1, Adult: 2 };
            if (
                groupOrder[r.minAgeGroup] &&
                groupOrder[r.maxAgeGroup] &&
                groupOrder[r.minAgeGroup] > groupOrder[r.maxAgeGroup]
            ) {
                newErr[`range_${i}_minAgeGroup`] = 'Min age group cannot be after Max age group';
            }
        });

        setErrors(newErr);
        return Object.keys(newErr).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            // Base fields
            const basePayload = {
                name: formData.parameterName.trim(),
                code: formData.parameterCode.trim(),
                unitId: selectedUnit?._id || null,
                unitValue: selectedUnit?.name || "",
                measurementType: formData.measurementType,
                referenceRanges: []
            };

            // Build referenceRanges dynamically based on measurementType
            const referenceRanges = formData.ranges.map(r => {
                const common = {
                    gender: r.gender === 'all' ? 'all' : r.gender.toLowerCase(),
                    // ageCategory: r.minAgeGroup || 'all' ? 'all' : r.minAgeGroup.toLowerCase(),
                    ageCategory: r.minAgeGroup && r.minAgeGroup !== 'all' ? r.minAgeGroup.toLowerCase() : 'all',
                    note: r.note?.trim() || ""
                };

                if (formData.measurementType === 'quantitative') {
                    return {
                        ...common,
                        minValue: r.minValue ? Number(r.minValue) : null,
                        maxValue: r.maxValue ? Number(r.maxValue) : null
                    };
                }

                if (formData.measurementType === 'qualitative') {
                    return {
                        ...common,
                        qualitativeValue: r.referenceText?.trim() || "Not Specified"
                    };
                }

                if (formData.measurementType === 'ratio') {
                    return {
                        ...common,
                        ratioValue: r.referenceText?.trim() || "Not Specified"
                    };
                }

                if (formData.measurementType === 'scale') {
                    return {
                        ...common,
                        scaleValue: r.referenceText?.trim() || "Not Specified"
                    };
                }

                return common;
            });

            const payload = {
                ...basePayload,
                referenceRanges
            };

            console.log("Final Payload →", payload); // Remove in production

            const res = await addParameter(payload);
            if (res.data.success) {
                toast.success(res.data.message || "Parameter added successfully!");
                navigate(`${process.env.PUBLIC_URL}/parameter-list`);
            }
        } catch (error) {
            const msg = error.response?.data?.message || "Failed to save parameter";
            toast.warning(msg);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleRadio = (e) => {
        const value = e.target.value;
        setImported(value);
    };

    // Handle Select Change with "Select All" logic
    const handleGlobalUnitChange = (selected) => {
        // "selected" comes from react-select (may include "Select All")
        if (!selected) {
            setSelectedGlobalUnits([]);
            return;
        }

        const hasSelectAll = selected.some(opt => opt.value === "*");

        if (hasSelectAll) {
            // Toggle Select All
            if (selectedGlobalUnits.length === unitMaster.length) {
                setSelectedGlobalUnits([]); // Deselect all
            } else {
                // Select all real units
                setSelectedGlobalUnits(unitMaster.map(u => ({
                    label: u.name,
                    value: u._id
                })));
            }
        } else {
            // Normal multi-select behavior
            // Filter out the "Select All" option from value
            const realSelections = selected.filter(opt => opt.value !== "*");
            setSelectedGlobalUnits(realSelections);
        }
    };

    const unitMasterOptions = unit.map((u) => ({
        value: u._id,
        label: u.name
    }));

    return (
        <>
            <Breadcrumbs mainTitle="Add Parameter" parent="Home" subParent="List Of Parameter" title="Add Parameter" />
            <div className="container">
                <div className="card shadow">
                    <div className="d-flex tw-space-x-4 px-4 pt-4">
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-add"
                                type="radio"
                                name="customerType"
                                value="add"
                                checked={imported === "add"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-add" className="mb-0 pe-2">
                                Manual
                            </Label>
                        </div>
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-gloabal"
                                type="radio"
                                name="customerType"
                                value="gloabal"
                                checked={imported === "gloabal"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-gloabal" className="mb-0 pe-2">
                                Import From Database
                            </Label>
                        </div>
                        {imported !== "add" && (
                            selectedGlobalUnits.length > 0 && (
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSubmitSelected}
                                    disabled={selectedGlobalUnits.length === 0}
                                >
                                    Import ({selectedGlobalUnits.length})
                                </button>
                            )
                        )}
                    </div>
                    {imported === "add" ? (
                        <div className="card-body p-4">
                            <form noValidate>

                                {/* ---------- Top Row: Name, Code, Type, Unit ---------- */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-3">
                                        <label className="form-label">
                                            Lab Paramater Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="parameterName"
                                            value={formData.parameterName}
                                            onChange={handleChange}
                                            placeholder="e.g., Hemoglobin"
                                            className={`form-control ${errors.parameterName ? 'is-invalid' : ''}`}
                                        />
                                        {errors.parameterName && (
                                            <div className="invalid-feedback">{errors.parameterName}</div>
                                        )}
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">
                                            Paramater Code <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="parameterCode"
                                            value={formData.parameterCode}
                                            onChange={handleChange}
                                            placeholder="e.g., HB"
                                            className={`form-control ${errors.parameterCode ? 'is-invalid' : ''}`}
                                        />
                                        {errors.parameterCode && (
                                            <div className="invalid-feedback">{errors.parameterCode}</div>
                                        )}
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">
                                            Measurement Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            name="measurementType"
                                            value={formData.measurementType}
                                            onChange={handleChange}
                                            className="form-select"
                                        >
                                            <option value="quantitative">Quantitative</option>
                                            <option value="qualitative">Qualitative</option>
                                            <option value="scale">Scale</option>
                                            <option value="ratio">Ratio</option>
                                        </select>
                                    </div>

                                    <div className="col-md-3">
                                        <label className="form-label">
                                            Unit
                                        </label>
                                        <Select
                                            options={unitMasterOptions}
                                            value={unitMasterOptions.find((opt) => opt.value === selectedUnit?._id) || null}
                                            onChange={(selected) => {
                                                const unitObj = unit.find((u) => u._id === selected.value);

                                                setSelectedUnit(unitObj);
                                                setFormData(prev => ({ ...prev, unit: selected.value }));
                                                setErrors(prev => ({ ...prev, unit: '' }));
                                            }}
                                            placeholder="Select Unit"
                                            classNamePrefix="react-select"
                                            className={errors.unit ? "is-invalid" : ""}
                                        />
                                        {/* <select
                                            value={selectedUnit?._id || ''}
                                            onChange={(e) => {
                                                const unitId = e.target.value;
                                                const unitObj = unit.find(u => u._id === unitId);
                                                setSelectedUnit(unitObj || null);
                                                setFormData(prev => ({ ...prev, unit: unitId })); // keep ID for validation
                                                setErrors(prev => ({ ...prev, unit: '' }));
                                            }}
                                            className={`form-select ${errors.unit ? 'is-invalid' : ''}`}
                                        >
                                            <option value="">Select Unit</option>
                                            {unit.map((data) => (
                                                <option key={data._id} value={data._id}>
                                                    {data.name}
                                                </option>
                                            ))}
                                        </select> */}
                                        {errors.unit && <div className="invalid-feedback">{errors.unit}</div>}
                                    </div>
                                </div>

                                <div className="d-flex gap-2 mt-4">
                                    <h5 className="mb-0">Reference Ranges</h5>
                                </div>

                                <div className="row g-4">
                                    {formData.ranges.map((range, idx) => (
                                        <div key={idx} className="col-12">
                                            <div className="border rounded p-4 position-relative bg-light text-black">

                                                {/* Delete Button */}
                                                {formData.ranges.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeRange(idx)}
                                                        className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 mt-2 me-2"
                                                    >
                                                        Remove
                                                    </button>
                                                )}

                                                <h6 className="mb-3">#{idx + 1}</h6>

                                                <div className="row g-3">
                                                    {/* Gender */}
                                                    <div className="col-lg-3 col-md-4">
                                                        <label className="form-label">Sex <span className="text-danger">*</span></label>
                                                        <select
                                                            value={range.gender}
                                                            onChange={e => handleRangeChange(idx, 'gender', e.target.value)}
                                                            className={`form-select ${errors[`range_${idx}_gender`] ? 'is-invalid' : ''}`}
                                                        >
                                                            <option value="">Select Gender</option>
                                                            <option value="all">All</option>
                                                            <option value="male">Male</option>
                                                            <option value="female">Female</option>
                                                        </select>
                                                        {errors[`range_${idx}_gender`] && (
                                                            <div className="invalid-feedback">{errors[`range_${idx}_gender`]}</div>
                                                        )}
                                                    </div>

                                                    {/* Min Age Group */}
                                                    <div className="col-lg-3 col-md-4">
                                                        <label className="form-label">Age <span className="text-danger">*</span></label>
                                                        <select
                                                            value={range.minAgeGroup}
                                                            onChange={e => handleRangeChange(idx, 'minAgeGroup', e.target.value)}
                                                            className={`form-select ${errors[`range_${idx}_minAgeGroup`] ? 'is-invalid' : ''}`}
                                                        >
                                                            <option value="">Select</option>
                                                            <option value="all">All</option>
                                                            <option value="child">Child</option>
                                                            <option value="adult">Adult</option>
                                                        </select>
                                                        {errors[`range_${idx}_minAgeGroup`] && (
                                                            <div className="invalid-feedback">{errors[`range_${idx}_minAgeGroup`]}</div>
                                                        )}
                                                    </div>
                                                    {/* === QUANTITATIVE: Min & Max === */}
                                                    {formData.measurementType === 'quantitative' && (
                                                        <>
                                                            <div className="col-lg-3 col-md-4">
                                                                <label className="form-label">Min Value <span className="text-danger">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    value={range.minValue}
                                                                    onChange={e => handleRangeChange(idx, 'minValue', e.target.value)}
                                                                    placeholder="e.g., 12"
                                                                    className={`form-control ${errors[`range_${idx}_minValue`] ? 'is-invalid' : ''}`}
                                                                />
                                                                {errors[`range_${idx}_minValue`] && <div className="invalid-feedback">{errors[`range_${idx}_minValue`]}</div>}
                                                            </div>
                                                            <div className="col-lg-3 col-md-4">
                                                                <label className="form-label">Max Value <span className="text-danger">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    value={range.maxValue}
                                                                    onChange={e => handleRangeChange(idx, 'maxValue', e.target.value)}
                                                                    placeholder="e.g., 18"
                                                                    className={`form-control ${errors[`range_${idx}_maxValue`] ? 'is-invalid' : ''}`}
                                                                />
                                                                {errors[`range_${idx}_maxValue`] && <div className="invalid-feedback">{errors[`range_${idx}_maxValue`]}</div>}
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* === SCALE & RATIO & QUALITATIVE: Text Reference === */}
                                                    {['scale', 'ratio', 'qualitative'].includes(formData.measurementType) && (
                                                        <>
                                                            {/* Main Reference Value (Required) */}
                                                            <div className="col-lg-6">
                                                                <label className="form-label">
                                                                    {formData.measurementType === 'scale' && 'Scale Values'}
                                                                    {formData.measurementType === 'ratio' && 'Ratio Thresholds'}
                                                                    {formData.measurementType === 'qualitative' && 'Possible Results'}
                                                                    <span className="text-danger">*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={range.referenceText || ''}
                                                                    onChange={e => handleRangeChange(idx, 'referenceText', e.target.value)}
                                                                    placeholder={
                                                                        formData.measurementType === 'scale'
                                                                            ? "e.g., Negative, Trace, 1+, 2+, 3+"
                                                                            : formData.measurementType === 'ratio'
                                                                                ? "e.g., <1:40 (Negative), ≥1:160 (Positive)"
                                                                                : "e.g., Positive, Negative, Reactive, Non-reactive"
                                                                    }
                                                                    className={`form-control ${errors[`range_${idx}_referenceText`] ? 'is-invalid' : ''} form-control`}
                                                                />
                                                                {errors[`range_${idx}_referenceText`] && (
                                                                    <div className="invalid-feedback">{errors[`range_${idx}_referenceText`]}</div>
                                                                )}
                                                                <small className="text-muted">
                                                                    Separate multiple values with commas
                                                                </small>
                                                            </div>
                                                        </>
                                                    )}

                                                    {/* <div className="col-lg-3 col-md-4">
                                                        <label className="form-label">Min Value <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            value={range.minValue}
                                                            onChange={e => handleRangeChange(idx, 'minValue', e.target.value)}
                                                            placeholder="e.g., 12"
                                                            className={`form-control ${errors[`range_${idx}_minValue`] ? 'is-invalid' : ''}`}
                                                        />
                                                        {errors[`range_${idx}_minValue`] && (
                                                            <div className="invalid-feedback">{errors[`range_${idx}_minValue`]}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-lg-3 col-md-4">
                                                        <label className="form-label">Max Value <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            value={range.maxValue}
                                                            onChange={e => handleRangeChange(idx, 'maxValue', e.target.value)}
                                                            placeholder="e.g., 18"
                                                            className={`form-control ${errors[`range_${idx}_maxValue`] ? 'is-invalid' : ''}`}
                                                        />
                                                        {errors[`range_${idx}_maxValue`] && (
                                                            <div className="invalid-feedback">{errors[`range_${idx}_maxValue`]}</div>
                                                        )}
                                                    </div> */}
                                                    {/* )} */}
                                                </div>

                                                {/* Note */}
                                                <div className="mt-3">
                                                    <label className="form-label">Note (Optional)</label>
                                                    <input
                                                        type="text"
                                                        value={range.note}
                                                        onChange={e => handleRangeChange(idx, 'note', e.target.value)}
                                                        placeholder="e.g., Low values may indicate anemia"
                                                        className="form-control"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="col-md-12 text-end">

                                        {/* ---------- Reference Ranges ---------- */}
                                        <div className="">
                                            <button
                                                type="button"
                                                onClick={addRange}
                                                className="btn btn-primary"
                                            >
                                                Add Range
                                            </button>
                                        </div>
                                    </div>
                                </div>


                                {/* ---------- Buttons ---------- */}
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <button onClick={handleSubmit} className="btn btn-primary">
                                        Save Parameter
                                    </button>
                                    <Link to={`${process.env.PUBLIC_URL}/parameter-list`}>
                                        <button className="btn btn-secondary">
                                            Cancel
                                        </button>
                                    </Link>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <>
                            <div className="p-4">
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    options={unitOptions}
                                    value={selectedGlobalUnits}
                                    onChange={handleGlobalUnitChange}
                                    placeholder="Add by global parameters..."
                                    components={{ Option }}
                                    styles={customStyles}
                                    classNamePrefix="react-select"
                                    isClearable={true}
                                />

                                {/* <div className="tw-flex tw-justify-end mt-3">
                                    <button
                                        className="btn btn-primary me-3"
                                        onClick={handleSubmitSelected}
                                        disabled={selectedGlobalUnits.length === 0}
                                    >
                                        Import ({selectedGlobalUnits.length})
                                    </button>

                                    <Button variant="secondary">
                                        Close
                                    </Button>

                                </div> */}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default AddReportForm;